import * as Sentry from '@sentry/react';

/**
 * Single function for "an error happened in a catch block I'd otherwise
 * have console.warn'd". Sends to Sentry with tags so the issue is
 * grep-able in the dashboard, and still logs to console in dev so the
 * local DX is unchanged.
 *
 * Use this instead of bare `console.warn` / `console.error` in any
 * catch block where the error means a pro / customer action didn't
 * fully take effect (quote save, offer write, payment intent) — those
 * are the ones worth knowing about in production. Pure local-state or
 * parse errors that don't affect the user are still fine as console-only.
 *
 * Pattern:
 *
 *   try {
 *     await supabase.from('customer_offer_codes').insert(...);
 *   } catch (err) {
 *     reportError(err, {
 *       scope: 'createOffer.headerInsert',
 *       userId: pro.id,
 *       extra: { customerEmail },
 *     });
 *   }
 *
 * Twin of apps/core/.../src/lib/observability.ts — kept per-app rather
 * than in packages/* because the @sentry/react dep is already in both
 * apps' workspace deps and there's no other shared observability work.
 */
export interface ReportErrorContext {
  /** Short, stable identifier for WHERE this fired. Used as a Sentry
   *  tag — keep it dot-namespaced (`hookName.actionName`) and don't
   *  embed any per-call data. */
  scope: string;
  /** Optional caller user ID. Sentry already has it from setUser, but
   *  pinning it on the breadcrumb makes filtering by user trivial. */
  userId?: string | null;
  /** Free-form per-call context that helps reproduce. NOT used for
   *  grouping. Anything sensitive (auth tokens, PII beyond ID/email)
   *  belongs OUT of here. */
  extra?: Record<string, unknown>;
}

export function reportError(
  err: unknown,
  context: ReportErrorContext,
): void {
  if (import.meta.env.MODE !== 'production') {
    console.warn(`[${context.scope}]`, err, context.extra ?? '');
  }
  try {
    Sentry.withScope((scope) => {
      scope.setTag('scope', context.scope);
      if (context.userId) scope.setUser({ id: context.userId });
      if (context.extra) {
        for (const [k, v] of Object.entries(context.extra)) {
          scope.setExtra(k, v);
        }
      }
      if (err instanceof Error) {
        Sentry.captureException(err);
      } else {
        Sentry.captureMessage(
          typeof err === 'string' ? err : JSON.stringify(err ?? 'unknown'),
          'error',
        );
      }
    });
  } catch {
    // Sentry itself errored. Don't propagate — observability never
    // should be the thing that breaks a user-facing flow.
  }
}

export function addBreadcrumb(
  message: string,
  data?: Record<string, unknown>,
): void {
  try {
    Sentry.addBreadcrumb({ message, data, level: 'info' });
  } catch {
    // No-op if SDK isn't initialized.
  }
}
